'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { offlineDB, type OfflineVerification, type OfflineBoothInfo } from '@/lib/offline-db';
import { useOfflineSync } from '@/hooks/use-offline-sync';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  FileText,
  Printer,
  Loader2,
  ArrowLeft,
  Building2,
  Calendar,
  Users,
  AlertCircle,
} from 'lucide-react';

export function Report14A() {
  const { token, user } = useAuthStore();
  const { setCurrentView, isOnline } = useAppStore();
  const { pendingCount } = useOfflineSync();

  const [boothInfo, setBoothInfo] = useState<OfflineBoothInfo | null>(null);
  const [verifications, setVerifications] = useState<OfflineVerification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reportDate, setReportDate] = useState('');

  const boothId = user?.boothId || '';

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await offlineDB.init();

        // Load booth info from IndexedDB
        const info = await offlineDB.getBoothInfo(boothId);
        if (info) {
          setBoothInfo(info);
        }

        // Load verifications from IndexedDB
        const localVerifications = await offlineDB.getVerifications(boothId);
        setVerifications(localVerifications);

        // If online, also try to fetch from API for complete data
        if (isOnline && token) {
          try {
            const res = await fetch(`/api/reports?boothId=${boothId}&type=14a`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              const data = await res.json();
              if (data.success && data.booth) {
                setBoothInfo((prev) => prev || {
                  pollingBoothId: data.booth.pollingBoothId,
                  name: data.booth.name,
                  address: data.booth.address || '',
                  district: data.booth.district || '',
                  constituency: data.booth.constituency || '',
                  constituencyNo: data.booth.constituencyNo || '',
                  wardNo: data.booth.wardNo || '',
                  totalVoters: data.booth.totalVoters || 0,
                  downloadedAt: new Date().toISOString(),
                });
              }
            }
          } catch {
            // API fetch failed, use offline data
          }
        }

        // Set report date
        const now = new Date();
        setReportDate(now.toLocaleDateString('hi-IN', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        }));
      } catch (error) {
        console.error('Failed to load report data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (boothId) {
      loadData();
    }
  }, [boothId, isOnline, token]);

  const downloadReport = useCallback(() => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Build verification table rows from data — clean HTML, no Tailwind/shadcn
    const tableRows = verifications.map((v, index) => {
      const relationLabel = v.relationType === 'पिता' ? 'पिता' : 'पति';
      const nameDetail = `${v.voterName}<br/><span style="font-size:10px;color:#444;">${relationLabel}: ${v.fatherHusbandName}<br/>लिंग: ${v.gender}, आयु: ${v.age}</span>`;

      let thumbSignatureHtml = '—';
      if (v.verificationType === 'signature' && v.signatureImage) {
        thumbSignatureHtml = `<img src="${v.signatureImage}" alt="हस्ताक्षर" style="max-width:50px;max-height:30px;" />`;
      } else if (v.thumbImage) {
        thumbSignatureHtml = `<img src="${v.thumbImage}" alt="अंगूठे का निशान" style="max-width:50px;max-height:30px;" />`;
      }

      const remark = v.synced === 0 ? 'लंबित सिंक' : '';

      return `<tr>
        <td style="border:1px solid #000;padding:5px 8px;text-align:center;">${index + 1}</td>
        <td style="border:1px solid #000;padding:5px 8px;text-align:center;">${v.serialNo}</td>
        <td style="border:1px solid #000;padding:5px 8px;text-align:center;font-family:monospace;font-size:11px;">${v.epicNo}</td>
        <td style="border:1px solid #000;padding:5px 8px;font-size:12px;">${nameDetail}</td>
        <td style="border:1px solid #000;padding:5px 8px;text-align:center;">${thumbSignatureHtml}</td>
        <td style="border:1px solid #000;padding:5px 8px;text-align:center;font-size:11px;">${remark}</td>
      </tr>`;
    }).join('');

    const maleCount = verifications.filter(v => v.gender === 'पुरुष').length;
    const femaleCount = verifications.filter(v => v.gender === 'महिला').length;

    const html = `<!DOCTYPE html>
<html lang="hi">
<head>
  <meta charset="UTF-8" />
  <title>फॉर्म - 14 ए | Form 14-A</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;500;600;700&display=swap" rel="stylesheet" />
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'Noto Sans Devanagari', Arial, sans-serif;
      color: #000;
      background: #fff;
      font-size: 13px;
      line-height: 1.5;
      padding: 20px;
      max-width: 780px;
      margin: 0 auto;
    }

    .form-header {
      text-align: center;
      border-bottom: 3px double #000;
      padding-bottom: 14px;
      margin-bottom: 18px;
    }

    .form-title {
      font-size: 22px;
      font-weight: 700;
      color: #000;
      margin-bottom: 4px;
    }

    .legal-ref {
      font-size: 11px;
      color: #333;
      margin-bottom: 6px;
    }

    .form-subtitle {
      font-size: 15px;
      font-weight: 600;
      color: #000;
      margin-bottom: 10px;
      text-decoration: underline;
    }

    .booth-info {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 6px 24px;
      font-size: 12px;
      margin-bottom: 4px;
    }

    .booth-info span {
      font-weight: 400;
    }

    .booth-info strong {
      font-weight: 600;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 14px;
    }

    th {
      border: 1px solid #000;
      padding: 7px 6px;
      text-align: center;
      background: #f0f0f0;
      font-weight: 600;
      font-size: 11px;
      color: #000;
    }

    td {
      border: 1px solid #000;
      padding: 5px 8px;
      font-size: 12px;
      vertical-align: middle;
    }

    .footer-section {
      margin-top: 40px;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      font-size: 12px;
    }

    .footer-section .sig-block {
      text-align: center;
    }

    .footer-section .sig-line {
      border-top: 1px solid #000;
      width: 180px;
      padding-top: 5px;
      margin-top: 40px;
      font-weight: 500;
    }

    .summary-line {
      font-size: 11px;
      color: #333;
      margin-top: 4px;
    }

    .empty-msg {
      text-align: center;
      padding: 40px 0;
      font-size: 13px;
      color: #555;
    }

    @media print {
      body {
        padding: 10px;
        max-width: 100%;
      }
      .form-header {
        border-bottom: 3px double #000;
      }
      th {
        background: #f0f0f0 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  </style>
</head>
<body>
  <div class="form-header">
    <div class="form-title">फॉर्म - 14 ए</div>
    <div class="legal-ref">म.प्र. निर्वाचन विधि, 1995 की धारा 72 अनुच्छेद (1)(ख) के तहत</div>
    <div class="form-subtitle">मतदाता रजिस्टर</div>
    <div class="booth-info">
      <span>निर्वाचन क्षेत्र न.: <strong>${boothInfo?.constituencyNo || '___'}</strong></span>
      <span>निर्वाचन क्षेत्र: <strong>${boothInfo?.constituency || '___'}</strong></span>
      <span>जिला: <strong>${boothInfo?.district || '___'}</strong></span>
      <span>मतदान कक्ष: <strong>${boothInfo?.name || '___'}</strong></span>
    </div>
    <div class="booth-info" style="margin-top:4px;">
      <span>दिनांक: <strong>${reportDate || '___'}</strong></span>
    </div>
  </div>

  ${verifications.length === 0 ? `<div class="empty-msg">कोई सत्यापन रिकॉर्ड नहीं मिला</div>` : `
  <table>
    <thead>
      <tr>
        <th style="width:40px;">क्रमांक<br/><span style="font-weight:400;">(1)</span></th>
        <th style="width:70px;">मतदाता<br/>अनुक्रमांक<br/><span style="font-weight:400;">(2)</span></th>
        <th style="width:90px;">EPiC नंबर<br/><span style="font-weight:400;">(3)</span></th>
        <th>मतदाता का नाम तथा<br/>पिता/पति का नाम<br/><span style="font-weight:400;">(4)</span></th>
        <th style="width:100px;">अंगुष्ठ छाप/<br/>हस्ताक्षर<br/><span style="font-weight:400;">(5)</span></th>
        <th style="width:80px;">टिप्पणी<br/><span style="font-weight:400;">(6)</span></th>
      </tr>
    </thead>
    <tbody>
      ${tableRows}
    </tbody>
  </table>
  `}

  <div class="summary-line">
    कुल सत्यापित मतदाता: <strong>${verifications.length}</strong>
    &nbsp;|&nbsp; पुरुष: <strong>${maleCount}</strong>
    &nbsp;|&nbsp; महिला: <strong>${femaleCount}</strong>
  </div>

  <div class="footer-section">
    <div class="sig-block">
      <div class="sig-line">दिनांक</div>
    </div>
    <div class="sig-block">
      <div class="sig-line">मतदान अधिकारी का हस्ताक्षर</div>
    </div>
    <div class="sig-block">
      <div class="sig-line">पीठासीन अधिकारी का हस्ताक्षर</div>
    </div>
  </div>
</body>
</html>`;

    printWindow.document.write(html);
    printWindow.document.close();

    // 500ms delay to allow Google Fonts to load before printing
    setTimeout(() => {
      printWindow.print();
    }, 500);
  }, [verifications, boothInfo, reportDate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-3">
          <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto" />
          <p className="text-emerald-800 font-medium">रिपोर्ट लोड हो रही है...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-4 space-y-4">
      {/* Header Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-6 h-6 text-emerald-700" />
          <div>
            <h2 className="text-lg font-bold text-emerald-900">फॉर्म - 14 ए</h2>
            <p className="text-xs text-gray-500">मतदाता रजिस्टर | Voter Register</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">
            <Users className="w-3 h-3 mr-1" />
            {verifications.length} सत्यापित
          </Badge>
          {pendingCount > 0 && (
            <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">
              {pendingCount} लंबित सिंक
            </Badge>
          )}
          <Button
            onClick={downloadReport}
            className="bg-emerald-600 hover:bg-emerald-700 text-white min-h-[44px]"
          >
            <Printer className="w-4 h-4 mr-2" />
            प्रिंट / डाउनलोड
          </Button>
        </div>
      </div>

      {/* Report Content - Printable */}
      <Card className="border-emerald-200 shadow-md" id="report-14a-content">
        <CardContent className="p-6">
          {/* Header Section */}
          <div className="report-header text-center mb-6 pb-4 border-b-4 border-double border-emerald-800">
            <h1 className="text-2xl font-bold text-emerald-900 mb-1">फॉर्म - 14 ए</h1>
            <p className="text-xs text-gray-600 mb-3">
              म.प्र. निर्वाचन विधि, 1995 की धारा 72 अनुच्छेद (1)(ख) के तहत
            </p>
            <p className="text-sm font-semibold text-emerald-800 mb-2">मतदाता रजिस्टर</p>

            <div className="flex items-center justify-center gap-6 text-sm flex-wrap">
              <span>
                <span className="text-gray-500">निर्वाचन क्षेत्र न.:</span>{' '}
                <span className="font-semibold">{boothInfo?.constituencyNo || '___'}</span>
              </span>
              <span>
                <span className="text-gray-500">निर्वाचन क्षेत्र:</span>{' '}
                <span className="font-semibold">{boothInfo?.constituency || '___'}</span>
              </span>
              <span>
                <span className="text-gray-500">जिला:</span>{' '}
                <span className="font-semibold">{boothInfo?.district || '___'}</span>
              </span>
            </div>

            <div className="flex items-center justify-center gap-4 mt-2 text-sm">
              <span>
                <span className="text-gray-500">मतदान कक्ष:</span>{' '}
                <span className="font-semibold">{boothInfo?.name || '___'}</span>
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3 text-gray-400" />
                <span className="text-gray-500">दिनांक:</span>{' '}
                <span className="font-semibold">{reportDate}</span>
              </span>
            </div>
          </div>

          {/* Verifications Table */}
          {verifications.length === 0 ? (
            <div className="text-center py-16">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">कोई सत्यापन रिकॉर्ड नहीं मिला</p>
              <p className="text-gray-400 text-sm mt-1">मतदाता सत्यापन के बाद यह रिपोर्ट उपलब्ध होगी</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto styled-scrollbar">
              <Table className="border border-emerald-800">
                <TableHeader>
                  <TableRow className="bg-emerald-100 hover:bg-emerald-100">
                    <TableHead className="border border-emerald-800 text-center text-emerald-900 font-bold w-[60px]">
                      क्रमांक
                    </TableHead>
                    <TableHead className="border border-emerald-800 text-center text-emerald-900 font-bold w-[80px]">
                      मतदाता<br />अनुक्रमांक
                    </TableHead>
                    <TableHead className="border border-emerald-800 text-center text-emerald-900 font-bold w-[100px]">
                      EPiC नंबर
                    </TableHead>
                    <TableHead className="border border-emerald-800 text-center text-emerald-900 font-bold min-w-[200px]">
                      मतदाता का नाम तथा<br />पिता/पति का नाम
                    </TableHead>
                    <TableHead className="border border-emerald-800 text-center text-emerald-900 font-bold w-[120px]">
                      अंगुष्ठ छाप /<br />हस्ताक्षर
                    </TableHead>
                    <TableHead className="border border-emerald-800 text-center text-emerald-900 font-bold w-[100px]">
                      टिप्पणी
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {verifications.map((v, index) => (
                    <TableRow key={v.id} className="hover:bg-emerald-50/50 even:bg-gray-50/50">
                      <TableCell className="border border-emerald-800/30 text-center font-medium">
                        {index + 1}
                      </TableCell>
                      <TableCell className="border border-emerald-800/30 text-center">
                        {v.serialNo}
                      </TableCell>
                      <TableCell className="border border-emerald-800/30 text-center font-mono text-xs">
                        {v.epicNo}
                      </TableCell>
                      <TableCell className="border border-emerald-800/30">
                        <div>
                          <p className="font-medium">{v.voterName}</p>
                          <p className="text-xs text-gray-500">
                            {v.relationType === 'पिता' ? 'पिता' : 'पति'}: {v.fatherHusbandName}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="border border-emerald-800/30 text-center">
                        {v.verificationType === 'signature' && v.signatureImage ? (
                          <img
                            src={v.signatureImage}
                            alt="हस्ताक्षर"
                            className="max-w-[50px] max-h-[30px] mx-auto object-contain rounded"
                          />
                        ) : v.thumbImage ? (
                          <img
                            src={v.thumbImage}
                            alt="अंगूठे का निशान"
                            className="max-w-[50px] max-h-[30px] mx-auto object-contain rounded"
                          />
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="border border-emerald-800/30 text-center text-xs text-gray-500">
                        {v.synced === 0 ? 'लंबित सिंक' : ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Footer / Signature Section */}
          <div className="mt-10 pt-6 border-t border-gray-300">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-gray-500 mb-1">कुल सत्यापित मतदाता: <span className="font-bold text-emerald-800">{verifications.length}</span></p>
                <p className="text-xs text-gray-500">पुरुष: <span className="font-medium">{verifications.filter(v => v.gender === 'पुरुष').length}</span> | महिला: <span className="font-medium">{verifications.filter(v => v.gender === 'महिला').length}</span></p>
              </div>
              <div className="text-center">
                <div className="w-48 border-t border-gray-800 pt-2 mt-8">
                  <p className="text-sm font-medium">मतदान अधिकारी का हस्ताक्षर</p>
                  <p className="text-xs text-gray-500 mt-1">Signature of Polling Officer</p>
                </div>
              </div>
              <div className="text-center">
                <div className="w-32 border-t border-gray-800 pt-2 mt-8">
                  <p className="text-sm font-medium">दिनांक</p>
                  <p className="text-xs text-gray-500 mt-1">Date</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Back Button */}
      <div className="flex justify-center pt-2 pb-4">
        <Button
          variant="outline"
          onClick={() => setCurrentView('officer-dashboard')}
          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          वापस जाएं
        </Button>
      </div>
    </div>
  );
}
